import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { CreateUserState } from './useCreateUser';

/** Password: typed or generated, temporary by default; then the account status switch. */
export const PasswordSection: React.FC<{ form: CreateUserState }> = ({ form }) => {
  const { t } = useI18n();
  const { errors, isLoading, password, confirmPassword, showPassword, strength } = form;
  const inputType = showPassword ? 'text' : 'password';

  return (
    <>
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('users.create.password')}</h3>
          <p>{t('users.create.passwordIntro')}</p>
          <div className="cu-section-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={form.fillGeneratedPassword} disabled={isLoading} data-testid="cu-generate-password">
              {t('users.create.generate')}
            </button>
            <button type="button" className="cu-link" onClick={() => form.setShowPassword(!showPassword)} aria-pressed={showPassword} data-testid="cu-toggle-password">
              {showPassword ? t('users.create.hide') : t('users.create.show')}
            </button>
          </div>
        </header>

        <div className="cu-grid">
          <div className="cu-field">
            <label htmlFor="cu-password">{t('users.create.password')}</label>
            <input id="cu-password" type={inputType} className={`cu-input mono${errors.password ? ' is-error' : ''}`} data-testid="field-user-password" value={password}
                   onChange={(e) => { form.setPassword(e.target.value); form.clearError('password'); form.clearError('confirmPassword'); }}
                   placeholder={t('users.passwordMinPlaceholder')} disabled={isLoading} autoComplete="new-password" />
            {password && (
              <span className={`cu-strength is-${strength}`} aria-label={t('users.create.strength')} data-testid="cu-strength">
                <i /><i /><i /><i />
                <span className="cu-strength-label">{t(`users.create.strength${strength}` as never)}</span>
              </span>
            )}
            {errors.password && <span className="cu-error" role="alert">{errors.password}</span>}
          </div>
          <div className="cu-field">
            <label htmlFor="cu-confirm">{t('users.confirmPassword')}</label>
            <input id="cu-confirm" type={inputType} className={`cu-input mono${errors.confirmPassword ? ' is-error' : ''}`} data-testid="field-user-confirm-password" value={confirmPassword}
                   onChange={(e) => { form.setConfirmPassword(e.target.value); form.clearError('confirmPassword'); }}
                   placeholder={t('users.confirmPasswordPlaceholder')} disabled={isLoading} autoComplete="new-password" />
            {errors.confirmPassword ? <span className="cu-error" role="alert">{errors.confirmPassword}</span>
              : password && confirmPassword && password === confirmPassword ? <span className="cu-hint is-ok">{t('users.create.passwordsMatch')}</span> : null}
          </div>
        </div>

        <label className="cu-check">
          <input type="checkbox" data-testid="field-user-temporary" checked={form.temporaryPassword} onChange={(e) => form.setTemporaryPassword(e.target.checked)} disabled={isLoading} />
          <span>{t('users.temporaryPassword')}</span>
        </label>
        {!password && <p className="cu-note">{t('users.create.noPasswordNote')}</p>}
      </section>

      <section className="cu-section cu-section-inline">
        <label className="cu-switch">
          <input type="checkbox" data-testid="field-user-enabled" checked={form.enabled} onChange={(e) => form.setEnabled(e.target.checked)} disabled={isLoading} />
          <span className="cu-switch-track" aria-hidden="true"><span className="cu-switch-thumb" /></span>
          <span className="cu-switch-text">
            <strong>{t('users.accountEnabled')}</strong>
            <span className="cu-hint">{t('users.create.enabledHelp')}</span>
          </span>
        </label>
      </section>
    </>
  );
};
